import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { ButtonMenu } from '@cld/button';
import { StylableComponentProps } from '@cld/common-definitions';
import CopyToClipboard from '@cld/copy-to-clipboard';
import { Icon } from '@cld/icon';
import { Tabs, Tab, Layout } from '@cld/tabs';
import { globalTheme } from '@cld/component-theme';
import { Code } from './Code';
import { Snippet } from './Snippet';

export interface CodeSnippetProps extends StylableComponentProps {
  onLanguageChange: (language: string) => void;
  activeLanguage?: string;
  snippets: Array<Snippet>;
  onCopy?: (snippet: Snippet) => void;
}

const CopyButtonWrapper = styled.div`
  position: absolute;
  top: ${globalTheme.spacing.xs};
  right: ${globalTheme.spacing.xs};
  transition: 0.3s opacity;
  opacity: 0;
  svg path {
    fill: ${globalTheme.palette.secondaryAlt};
  }
  button {
    background: ${globalTheme.palette.surfaceAlt};
  }
`;

const CodeWrapper = styled.div`
  position: relative;
  margin: ${globalTheme.spacing.xs} 0;
  &:hover ${CopyButtonWrapper}, ${CopyButtonWrapper}:focus-within {
    opacity: 1;
  }
`;

const StyledCode = styled(Code)`
  pre {
    height: 100px;
  }
`;

export const CodeSnippet = styled<FC<CodeSnippetProps>>(({ snippets, activeLanguage, onLanguageChange, className, onCopy }) => {
  const activeLanguageObject = useMemo(() => (activeLanguage === undefined ? snippets[0] : snippets.find((s) => s.language === activeLanguage)), [
    snippets,
    activeLanguage,
  ]);

  if (!snippets.length) {
    return <>No snippets provided</>;
  }
  if (!activeLanguageObject) {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    throw new Error(`CodeSnippet doesn't support language ${activeLanguage}. Please specify snippet with such syntax in snippets property.`);
  }

  const tabId = useMemo(uuidv4, []);
  const handleCopy = () => onCopy?.(activeLanguageObject);

  return (
    <div className={className}>
      <Tabs variant="thin" layout={Layout.INLINE} activeTabId={snippets.indexOf(activeLanguageObject)} >
        {snippets.map((snippet, index) => {
          const onClick = () => onLanguageChange(snippet.language);
          return (
            <Tab
              key={snippet.language}
              id={index}
              onClick={onClick}
              aria-controls={`tab-${tabId}-${snippet.language}`}
            >
              {snippet.language}
            </Tab>
          );
        })}
      </Tabs>
      <CodeWrapper id={`tab-${tabId}-${activeLanguageObject.language}`}>
        <StyledCode syntax={activeLanguageObject.syntax}>{activeLanguageObject.snippet}</StyledCode>
        <CopyButtonWrapper>
          <CopyToClipboard
            tooltipBeforeText="Copy snippet"
            tooltipAfterText="Snippet copied to clipboard"
            copiedValue={activeLanguageObject.snippet}
            onCopy={handleCopy}
          >
            <ButtonMenu data-test="copy-snippet-button">
              <Icon name="copy" />
            </ButtonMenu>
          </CopyToClipboard>
        </CopyButtonWrapper>
      </CodeWrapper>
    </div>
  );
})``;
